"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { MessageCircle, Plus } from "lucide-react";
import { TicketUpdateForm } from "./TicketUpdateForm";

interface QuickTicketUpdateActionProps {
  ticketId: string;
  ticketNumber?: string;
  variant?: "button" | "icon";
  size?: "sm" | "lg" | "default";
  className?: string;
}

/**
 * Inline "Add Update" button + modal. Same form as the global add-update
 * modal (TicketUpdateForm) — local open state so it can sit anywhere a
 * ticket is shown without the global modal stack.
 */
export const QuickTicketUpdateAction = ({
  ticketId,
  ticketNumber,
  variant = "button",
  size = "sm",
  className = "",
}: QuickTicketUpdateActionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const buttonContent =
    variant === "icon" ? (
      <MessageCircle className="w-4 h-4" />
    ) : (
      <>
        <Plus className="w-4 h-4" />
        <span className="hidden sm:inline">Add Update</span>
      </>
    );

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        size={size}
        variant="outline"
        className={`flex items-center gap-2 ${className}`}
        title={variant === "icon" ? "Add ticket update" : undefined}
      >
        {buttonContent}
      </Button>

      <ResponsiveModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title={`Add Update${ticketNumber ? ` - ${ticketNumber}` : ""}`}
        maxWidth="lg"
      >
        <TicketUpdateForm
          ticketId={ticketId}
          onSuccess={() => setIsOpen(false)}
          onCancel={() => setIsOpen(false)}
        />
      </ResponsiveModal>
    </>
  );
};
